import { useMemo, useState } from 'react';
import { marked } from 'marked';

// Markdown sources live in docs/legal/ (so they're plain editable files outside
// the React tree, also viewable on GitHub). Vite's `?raw` suffix inlines them
// as strings at build time — zero runtime fetch, zero round-trip.
import privacyMd from '../../docs/legal/privacy_policy.md?raw';
import termsMd from '../../docs/legal/terms_of_service.md?raw';
import cookieMd from '../../docs/legal/cookie_policy.md?raw';

export type LegalDoc = 'privacy' | 'terms' | 'cookies';
type Lang = 'en' | 'it';

const DOCS: Record<LegalDoc, { title: string; source: string }> = {
  privacy: { title: 'Privacy Policy', source: privacyMd },
  terms:   { title: 'Terms of Service', source: termsMd },
  cookies: { title: 'Cookie Policy', source: cookieMd },
};

// Each legal doc is a single markdown file structured as:
//   <intro paragraphs>
//   ## English
//   ...
//   ## Italiano
//   ...
// This helper splits on the language headers and returns just the requested
// language slice. The pre-`## English` preamble (title + version) is always
// kept so the version stamp shows in both languages.
function sliceByLanguage(md: string, lang: Lang): string {
  const enIdx = md.indexOf('## English');
  const itIdx = md.indexOf('## Italiano');
  if (enIdx < 0 || itIdx < 0) return md; // fallback: show the whole thing
  const preamble = md.slice(0, enIdx).trim();
  const enSection = md.slice(enIdx, itIdx).replace(/^---\s*$/m, '').trim();
  const itSection = md.slice(itIdx).replace(/^---\s*$/m, '').trim();
  return `${preamble}\n\n${lang === 'en' ? enSection : itSection}`;
}

// Configure marked once. We render trusted content (we wrote it), so we use
// the sync `marked.parse` and trust the markdown — no DOMPurify needed.
marked.setOptions({ gfm: true, breaks: false });

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const [lang, setLang] = useState<Lang>(() => {
    // Default to browser language if it starts with `it`; otherwise English.
    const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
    return nav.toLowerCase().startsWith('it') ? 'it' : 'en';
  });
  // True when this tab has no meaningful back history — typical of pages
  // opened via target="_blank" or pasted URL. We use it to swap the "← Back"
  // affordance for a "✕ Close" affordance (and try window.close() instead
  // of navigating to '/', which would clone the app into the new tab).
  const [isStandalone] = useState(() => typeof window !== 'undefined' && window.history.length <= 1);
  const entry = DOCS[doc];
  const html = useMemo(() => {
    const slice = sliceByLanguage(entry.source, lang);
    return marked.parse(slice) as string;
  }, [entry.source, lang]);

  const handleBack = () => {
    if (!isStandalone) {
      window.history.back();
      return;
    }
    // Tab was opened standalone (target="_blank" or direct URL). Best effort
    // close — browsers allow window.close() on tabs that were opened by a
    // script-equivalent action (target="_blank" qualifies in current Chrome/
    // Edge). If the close is blocked, document.hidden stays false and we
    // navigate as a fallback so the user isn't stranded.
    window.close();
    setTimeout(() => {
      if (!document.hidden) window.location.href = '/';
    }, 200);
  };

  return (
    <div style={{
      height: '100vh', overflowY: 'auto',
      background: 'var(--bg-color)', color: 'var(--text-color)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 16px',
      boxSizing: 'border-box',
    }}>
      <div style={{ width: '100%', maxWidth: 820 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
          <button
            onClick={handleBack}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: 'var(--accent-color)', textDecoration: 'underline',
              fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >{isStandalone ? '✕ Close this tab' : '← Back'}</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <LangButton active={lang === 'en'} label="EN" onClick={() => setLang('en')} />
            <LangButton active={lang === 'it'} label="IT" onClick={() => setLang('it')} />
          </div>
        </div>
        <article
          className="legal-doc"
          // Trusted source — markdown lives in this repo and is authored by us.
          dangerouslySetInnerHTML={{ __html: html }}
          style={{
            background: '#15151f', border: '1px solid #2a2a3a',
            borderRadius: 6, padding: '24px 32px',
            lineHeight: 1.65, fontSize: 14,
          }}
        />
        <div style={{ marginTop: 24, marginBottom: 32, display: 'flex', gap: 12, fontSize: 12, flexWrap: 'wrap' }}>
          {(['privacy', 'terms', 'cookies'] as const).map(d => (
            <a key={d} href={`/legal/${d}`} style={{
              color: d === doc ? 'var(--accent-color)' : '#4fc3f7',
              textDecoration: d === doc ? 'none' : 'underline',
              fontWeight: d === doc ? 'bold' : 'normal',
            }}>{DOCS[d].title}</a>
          ))}
        </div>
      </div>
    </div>
  );
}

function LangButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? 'var(--accent-color)' : 'transparent',
        color: active ? '#000' : '#ccc',
        border: '1px solid #444',
        padding: '4px 12px',
        borderRadius: 4,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 'bold',
      }}
    >{label}</button>
  );
}
